import React, { Fragment, useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Typography,
  IconButton,
} from "@mui/material";
import withStyles from '@mui/styles/withStyles';
import CloseIcon from "@mui/icons-material/Close";

const styles = (theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#1579AB",
    color: "white",
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  paidChip: {
    backgroundColor: "#4caf50",
    color: "white",
    fontWeight: "bold",
  },
  unpaidChip: {
    backgroundColor: "#f44336",
    color: "white",
    fontWeight: "bold",
  },
  receiptUploadedChip: {
    backgroundColor: "#ff9800",
    color: "white",
    fontWeight: "bold",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  noDataContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
});

const FeeVouchersDialog = (props) => {
  const { classes, open, handleClose } = props;
  const [vouchers, setVouchers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchVouchers();
    }
  }, [open]);

  const fetchVouchers = async () => {
    setIsLoading(true);
    const url = `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/lms/C116FinanceStudentsLegacyFeeVouchersListView`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: new Headers({
          Authorization: "Bearer " + localStorage.getItem("lmsToken"),
        }),
      });

      if (!response.ok) {
        throw response;
      }

      const json = await response.json();

      if (json.CODE === 1) {
        setVouchers(json.DATA || []);
      } else {
        console.error("Failed to fetch vouchers:", json);
        setVouchers([]);
      }
    } catch (error) {
      console.error("Error fetching vouchers:", error);
      setVouchers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusChip = (voucher) => {
    const isConfirmed = voucher.isConfirmed === 1;
    const isUploaded = voucher.isUploaded === 1;

    if (isConfirmed) {
      return (
        <Chip
          label="Paid"
          className={classes.paidChip}
          size="small"
        />
      );
    } else if (isUploaded) {
      return (
        <Chip
          label="Receipt Uploaded"
          className={classes.receiptUploadedChip}
          size="small"
        />
      );
    } else {
      return (
        <Chip
          label="Unpaid"
          className={classes.unpaidChip}
          size="small"
        />
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="fee-vouchers-dialog-title"
    >
      <DialogTitle id="fee-vouchers-dialog-title" className={classes.dialogTitle}>
        <Typography variant="h6">Fee Vouchers</Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          style={{ color: "white" }}
          size="large">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : vouchers.length === 0 ? (
          <div className={classes.noDataContainer}>
            <Typography variant="body1" color="textSecondary">
              No fee vouchers found.
            </Typography>
          </div>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell><strong>Voucher No</strong></TableCell>
                  <TableCell><strong>Due Date</strong></TableCell>
                  <TableCell align="right"><strong>Amount</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                  <TableCell><strong>Installment</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vouchers.map((voucher, index) => (
                  <TableRow key={voucher.id || index}>
                    <TableCell>{voucher.voucherNo || voucher.billNo || "N/A"}</TableCell>
                    <TableCell>{voucher.dueDate || "N/A"}</TableCell>
                    <TableCell align="right">
                      {voucher.totalAmount || voucher.billAmount || "0"}
                    </TableCell>
                    <TableCell align="center">
                      {getStatusChip(voucher)}
                    </TableCell>
                    <TableCell>{voucher.installment || "N/A"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

FeeVouchersDialog.propTypes = {
  classes: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default withStyles(styles)(FeeVouchersDialog);
